import { useMemo } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, BarChart3, Eye, Users, Vote, ExternalLink, ChevronRight } from 'lucide-react';
import {
  aggregateArticlePollStats,
  normalizeBlogArticle,
  type BlogPollVoteRecord,
} from '@jeraisonne/db';
import { useAdminBlogArticle, useArticlePollVotes } from '@/lib/admin/use-blog';

const SITE_URL =
  (import.meta.env.VITE_MARKETING_URL as string | undefined)?.replace(/\/$/, '') ?? '';

function StatCard({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Users;
  label: string;
  value: number;
  tone: string;
}) {
  return (
    <div className="border-ink-200 rounded-2xl border bg-white p-4">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${tone}`} />
        <span className="font-display text-ink-500 text-[11px] font-semibold uppercase tracking-wide">
          {label}
        </span>
      </div>
      <p className="font-display text-ink-900 mt-1 text-[24px] font-extrabold">{value}</p>
    </div>
  );
}

export function TutorBlogStatsPage() {
  const { articleId } = useParams({ strict: false }) as { articleId: string };
  const { data: rawArticle, isLoading } = useAdminBlogArticle(articleId);
  const { data: votes = [], isLoading: votesLoading } = useArticlePollVotes(articleId);

  const article = useMemo(
    () => (rawArticle ? normalizeBlogArticle(rawArticle) : null),
    [rawArticle],
  );

  const choiceCounts = useMemo(
    () =>
      (article?.blocks ?? []).map((b) =>
        Array.isArray(b.poll?.choices) ? b.poll!.choices.length : 0,
      ),
    [article],
  );

  const stats = useMemo(
    () => aggregateArticlePollStats(votes as BlogPollVoteRecord[], choiceCounts),
    [votes, choiceCounts],
  );

  if (isLoading || !article) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  const isPublished = article.status === 'published' && article.visibility === 'public';

  return (
    <div className="mx-auto max-w-2xl p-4 pb-16 sm:p-6">
      <Link
        to="/tutor/blog/$articleId"
        params={{ articleId }}
        className="text-ink-500 hover:text-ink-900 mb-4 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Éditer l&apos;article
      </Link>

      <div className="mb-1 flex flex-wrap items-center gap-2">
        <h1 className="font-display text-ink-900 flex items-center gap-2 text-[20px] font-extrabold">
          <BarChart3 className="h-5 w-5" />
          Statistiques
        </h1>
        {isPublished && SITE_URL && (
          <a
            href={`${SITE_URL}/blog/${article.slug}`}
            target="_blank"
            rel="noreferrer"
            className="text-primary-600 inline-flex items-center gap-1 font-sans text-[12px] hover:underline"
          >
            Voir <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      <p className="text-ink-600 mb-5 font-sans text-[14px]">{article.title}</p>

      {/* Synthèse chiffrée */}
      <div className="mb-4 grid grid-cols-3 gap-3">
        <StatCard icon={Eye} label="Vues" value={article.view_count} tone="text-ink-500" />
        <StatCard
          icon={Users}
          label="Répondants"
          value={stats.respondentCount}
          tone="text-xp-600"
        />
        <StatCard icon={Vote} label="Votes" value={stats.totalVotes} tone="text-level-500" />
      </div>

      {/* Accès à la liste des inscrits ayant répondu */}
      <Link
        to="/tutor/admin/newsletter"
        search={{ article: articleId }}
        className="border-primary-200 bg-primary-50/60 hover:bg-primary-50 group mb-6 flex items-center gap-3 rounded-2xl border p-4 transition-colors active:scale-[0.99]"
      >
        <Users className="text-primary-600 h-5 w-5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-display text-ink-900 text-[14px] font-bold">
            Voir les inscrits ayant répondu
          </p>
          <p className="text-ink-500 font-sans text-[12px]">
            {stats.respondentCount} abonné{stats.respondentCount !== 1 ? 's' : ''} newsletter ·
            ouvre la liste filtrée
          </p>
        </div>
        <ChevronRight className="text-primary-400 group-hover:text-primary-600 h-5 w-5 shrink-0 transition-colors" />
      </Link>

      {/* Distribution par sondage */}
      <h2 className="font-display text-ink-900 mb-3 text-[15px] font-extrabold">
        Réponses aux sondages
      </h2>

      {votesLoading ? (
        <div className="flex items-center justify-center py-10">
          <div className="border-primary-500 h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : stats.perBlock.length === 0 ? (
        <div className="border-ink-200 text-ink-500 rounded-2xl border bg-white p-6 text-center font-sans text-[13px]">
          Aucun sondage sur cet article.
        </div>
      ) : (
        <div className="space-y-4">
          {stats.perBlock.map(({ blockIndex, distribution }) => {
            const block = article.blocks[blockIndex];
            const poll = block?.poll;
            return (
              <section
                key={blockIndex}
                className="border-ink-200 rounded-2xl border bg-white p-4 sm:p-5"
              >
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="font-display text-ink-400 text-[11px] font-bold uppercase tracking-wide">
                    Sondage {blockIndex + 1}
                  </span>
                  <span className="text-ink-400 font-sans text-[12px]">
                    {distribution.total} vote{distribution.total !== 1 ? 's' : ''}
                  </span>
                </div>
                <p className="font-display text-ink-900 mb-3 text-[14px] font-semibold">
                  {poll?.question || block?.heading || `Bloc ${blockIndex + 1}`}
                </p>

                <div className="space-y-2.5">
                  {(poll?.choices ?? []).map((choice, ci) => {
                    const count = distribution.counts[ci] ?? 0;
                    const pct = distribution.percentages[ci] ?? 0;
                    return (
                      <div key={ci}>
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <span className="text-ink-700 font-sans text-[13px]">
                            {choice || `Choix ${ci + 1}`}
                          </span>
                          <span className="text-ink-500 font-sans text-[12px] tabular-nums">
                            {count} · {pct}%
                          </span>
                        </div>
                        <div className="bg-ink-100 h-2 w-full overflow-hidden rounded-full">
                          <div
                            className="bg-primary-500 h-full rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {distribution.total > 0 && (
                  <Link
                    to="/tutor/admin/newsletter"
                    search={{ article: articleId, block: blockIndex }}
                    className="text-primary-600 hover:text-primary-700 mt-3 inline-flex items-center gap-1 font-sans text-[12px] font-semibold"
                  >
                    Voir les répondants à ce sondage
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
